import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, IconButton, Tooltip, Chip } from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import FastRewindRoundedIcon from '@mui/icons-material/FastRewindRounded';
import FastForwardRoundedIcon from '@mui/icons-material/FastForwardRounded';
import ContentCutRoundedIcon from '@mui/icons-material/ContentCutRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import VolumeOffRoundedIcon from '@mui/icons-material/VolumeOffRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded';
import LayersRoundedIcon from '@mui/icons-material/LayersRounded';
import MovieFilterRoundedIcon from '@mui/icons-material/MovieFilterRounded';
import AudiotrackRoundedIcon from '@mui/icons-material/AudiotrackRounded';
import { useTheme } from '../../../ThemeContext';

/**
 * Format a progress decimal (0..1) into SMPTE Timecode (HH:MM:SS:FF) at 60 FPS
 * Sequence duration = 180 seconds (3 minutes)
 */
const formatTimecode = (progress) => {
  const totalFrames = Math.floor(Math.max(0, Math.min(1, progress)) * 180 * 60);
  const frames = totalFrames % 60;
  const totalSeconds = Math.floor(totalFrames / 60);
  const seconds = totalSeconds % 60;
  const minutes = Math.floor((totalSeconds / 60) % 60);
  const hours = Math.floor(totalSeconds / 3600);

  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}:${pad(frames)}`;
};

/**
 * Interactive NLE Multi-Track Timeline Scrubber Component for Filmhook Editor
 */
export const FilmhookNLETimelineScrubber = ({ onScrub, activeShotIdx }) => {
  const { isDarkMode } = useTheme();

  // Playhead state (0..100)
  const [progress, setProgress] = useState(28.5);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [hoverPos, setHoverPos] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [cutPoints, setCutPoints] = useState([18, 44, 72]);
  const [tracksMuted, setTracksMuted] = useState({ v2: false, v1: false, a1: false, a2: false });
  const [tracksLocked, setTracksLocked] = useState({ v2: false, v1: false, a1: false, a2: false });

  const timelineRef = useRef(null);
  const animFrameRef = useRef(null);
  const lastTimeRef = useRef(null);

  // Playhead smooth animation loop
  const updatePlayback = useCallback(
    (now) => {
      if (!lastTimeRef.current) lastTimeRef.current = now;
      const delta = (now - lastTimeRef.current) / 1000;
      lastTimeRef.current = now;

      setProgress((prev) => {
        // Full sequence = 30 seconds nominal loop in player
        const step = (delta / 30) * 100 * playbackSpeed;
        const next = prev + step;
        if (next >= 100) return 0;
        return next;
      });

      if (isPlaying) {
        animFrameRef.current = requestAnimationFrame(updatePlayback);
      }
    },
    [isPlaying, playbackSpeed]
  );

  useEffect(() => {
    if (isPlaying) {
      lastTimeRef.current = performance.now();
      animFrameRef.current = requestAnimationFrame(updatePlayback);
    } else {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      lastTimeRef.current = null;
    }
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [isPlaying, updatePlayback]);

  // Notify parent on scrub
  useEffect(() => {
    if (onScrub) {
      onScrub(progress / 100);
    }
  }, [progress, onScrub]);

  // Interactive scrubber calculations
  const handleScrubMove = (clientX) => {
    if (!timelineRef.current) return;
    const rect = timelineRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setProgress(pct);
  };

  const handleMouseMove = (e) => {
    if (!timelineRef.current) return;
    const rect = timelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setHoverPos(pct);
    if (isDragging) {
      handleScrubMove(e.clientX);
    }
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    handleScrubMove(e.clientX);
  };

  const handleTouchMove = (e) => {
    if (e.touches && e.touches[0]) {
      handleScrubMove(e.touches[0].clientX);
    }
  };

  useEffect(() => {
    const handleGlobalMouseUp = () => setIsDragging(false);
    const handleGlobalMouseMove = (e) => {
      if (isDragging) handleScrubMove(e.clientX);
    };

    window.addEventListener('mouseup', handleGlobalMouseUp);
    window.addEventListener('mousemove', handleGlobalMouseMove);
    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('mousemove', handleGlobalMouseMove);
    };
  }, [isDragging]);

  const handleAddCut = () => {
    if (!cutPoints.includes(Math.round(progress))) {
      setCutPoints((prev) => [...prev, Math.round(progress)].sort((a, b) => a - b));
    }
  };

  const toggleSpeed = () => {
    setPlaybackSpeed((prev) => (prev === 1 ? 1.5 : prev === 1.5 ? 2 : 1));
  };

  const timecodeString = formatTimecode(progress / 100);
  const hoverTimecodeString = hoverPos !== null ? formatTimecode(hoverPos / 100) : null;

  return (
    <Box
      sx={{
        width: '100%',
        mt: 4,
        pt: 3,
        borderTop: isDarkMode
          ? '1px solid rgba(139, 92, 246, 0.25)'
          : '1px solid rgba(139, 92, 246, 0.15)',
      }}
    >
      {/* Container Card */}
      <Box
        sx={{
          borderRadius: '20px',
          background: isDarkMode
            ? 'linear-gradient(180deg, #090e1a 0%, #050811 100%)'
            : 'linear-gradient(180deg, #f8fafc 0%, #edf2f7 100%)',
          border: isDarkMode
            ? '1.5px solid rgba(139, 92, 246, 0.35)'
            : '1.5px solid rgba(139, 92, 246, 0.25)',
          boxShadow: isDarkMode
            ? '0 18px 48px rgba(0, 0, 0, 0.65), 0 0 35px rgba(139, 92, 246, 0.15)'
            : '0 16px 36px rgba(15, 23, 42, 0.08)',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* ══════════════════════════════════════════════════════════
            1. TOP TOOLBAR & TRANSPORT CONTROLS
            ══════════════════════════════════════════════════════════ */}
        <Box
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 1.5,
            borderBottom: isDarkMode
              ? '1px solid rgba(255, 255, 255, 0.08)'
              : '1px solid rgba(0, 0, 0, 0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
            background: isDarkMode ? 'rgba(15, 23, 42, 0.5)' : '#ffffff',
          }}
        >
          {/* Left: Transport Buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <Tooltip title="Rewind to Start (00:00:00:00)">
              <IconButton
                size="small"
                onClick={() => setProgress(0)}
                sx={{
                  color: isDarkMode ? '#94a3b8' : '#64748b',
                  '&:hover': { color: '#8b5cf6', bgcolor: 'rgba(139, 92, 246, 0.12)' },
                }}
              >
                <FastRewindRoundedIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>

            {/* Main Play/Pause Button */}
            <Tooltip title={isPlaying ? 'Pause Timeline' : 'Play Sequence (Spacebar)'}>
              <IconButton
                onClick={() => setIsPlaying(!isPlaying)}
                sx={{
                  width: 38,
                  height: 38,
                  background: isPlaying
                    ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                    : 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
                  color: '#ffffff',
                  boxShadow: isPlaying
                    ? '0 4px 14px rgba(239, 68, 68, 0.4)'
                    : '0 4px 16px rgba(139, 92, 246, 0.45)',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  },
                }}
              >
                {isPlaying ? (
                  <PauseRoundedIcon sx={{ fontSize: 22 }} />
                ) : (
                  <PlayArrowRoundedIcon sx={{ fontSize: 22 }} />
                )}
              </IconButton>
            </Tooltip>

            <Tooltip title="Forward 15 Seconds">
              <IconButton
                size="small"
                onClick={() => setProgress((prev) => Math.min(100, prev + 8.33))}
                sx={{
                  color: isDarkMode ? '#94a3b8' : '#64748b',
                  '&:hover': { color: '#8b5cf6', bgcolor: 'rgba(139, 92, 246, 0.12)' },
                }}
              >
                <FastForwardRoundedIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>

            {/* Razor Cut Tool */}
            <Tooltip title="Razor Blade Tool (Split Clip at Playhead)">
              <IconButton
                size="small"
                onClick={handleAddCut}
                sx={{
                  color: isDarkMode ? '#c084fc' : '#7c3aed',
                  bgcolor: isDarkMode ? 'rgba(139, 92, 246, 0.12)' : 'rgba(139, 92, 246, 0.08)',
                  borderRadius: '10px',
                  ml: 0.5,
                  '&:hover': {
                    bgcolor: 'rgba(139, 92, 246, 0.25)',
                  },
                }}
              >
                <ContentCutRoundedIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>

            {/* Playback Speed */}
            <Tooltip title={`Current Speed: ${playbackSpeed}x (Click to cycle)`}>
              <Chip
                icon={<SpeedRoundedIcon sx={{ fontSize: '15px !important', color: '#8b5cf6' }} />}
                label={`${playbackSpeed}x`}
                size="small"
                onClick={toggleSpeed}
                sx={{
                  fontWeight: 800,
                  fontSize: '0.72rem',
                  bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : '#f1f5f9',
                  color: isDarkMode ? '#e2e8f0' : '#1e293b',
                  cursor: 'pointer',
                  border: '1px solid rgba(139, 92, 246, 0.25)',
                  '&:hover': {
                    bgcolor: 'rgba(139, 92, 246, 0.15)',
                  },
                }}
              />
            </Tooltip>
          </Box>

          {/* Center: Glowing SMPTE Timecode Display */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              background: isDarkMode ? '#020617' : '#0f172a',
              px: 2,
              py: 0.6,
              borderRadius: '12px',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.5)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: isPlaying ? '#22c55e' : '#eab308',
                  boxShadow: isPlaying ? '0 0 10px #22c55e' : '0 0 8px #eab308',
                  animation: isPlaying ? 'pulse 1s infinite' : 'none',
                }}
              />
              <Typography
                sx={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  fontWeight: 900,
                  fontSize: { xs: '0.92rem', sm: '1.05rem' },
                  letterSpacing: '0.08em',
                  color: '#38bdf8',
                  textShadow: '0 0 10px rgba(56, 189, 248, 0.5)',
                }}
              >
                {timecodeString}
              </Typography>
            </Box>

            <Typography
              variant="caption"
              sx={{
                color: '#64748b',
                fontWeight: 700,
                fontSize: '0.68rem',
                borderLeft: '1px solid rgba(255,255,255,0.15)',
                pl: 1.2,
                display: { xs: 'none', sm: 'block' },
              }}
            >
              60 FPS • 4K PRORES HQ
            </Typography>
          </Box>

          {/* Right: Master Audio VU Meter & Status */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* Animated Audio VU Bars */}
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: 22, px: 1 }}>
              {[0.4, 0.7, 0.9, 0.6, 0.8, 0.5, 0.95, 0.3].map((heightRatio, i) => {
                const activeHeight = isPlaying && !isMuted ? Math.sin(progress * 0.3 + i) * 6 + 12 : 4;
                const barColor = i >= 6 ? '#ef4444' : i >= 4 ? '#eab308' : '#10b981';
                return (
                  <Box
                    key={i}
                    sx={{
                      width: 3.5,
                      height: `${activeHeight}px`,
                      bgcolor: barColor,
                      borderRadius: '1px',
                      transition: 'height 0.12s ease',
                      boxShadow: isPlaying ? `0 0 6px ${barColor}` : 'none',
                    }}
                  />
                );
              })}
            </Box>

            {/* Mute Toggle */}
            <Tooltip title={isMuted ? 'Unmute Audio Track' : 'Mute Master Audio'}>
              <IconButton
                size="small"
                onClick={() => setIsMuted(!isMuted)}
                sx={{
                  color: isMuted ? '#ef4444' : isDarkMode ? '#94a3b8' : '#64748b',
                  bgcolor: isMuted ? 'rgba(239, 68, 68, 0.12)' : 'transparent',
                }}
              >
                {isMuted ? (
                  <VolumeOffRoundedIcon sx={{ fontSize: 18 }} />
                ) : (
                  <VolumeUpRoundedIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </Tooltip>

            <Chip
              label="Interactive Timeline"
              size="small"
              sx={{
                bgcolor: 'rgba(139, 92, 246, 0.12)',
                color: '#a855f7',
                fontWeight: 800,
                fontSize: '0.68rem',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                display: { xs: 'none', md: 'inline-flex' },
              }}
            />
          </Box>
        </Box>

        {/* ══════════════════════════════════════════════════════════
            2. TIMELINE RULER & FRAMES SCALE
            ══════════════════════════════════════════════════════════ */}
        <Box sx={{ display: 'flex', width: '100%', bgcolor: isDarkMode ? '#0a0f1d' : '#f1f5f9' }}>
          {/* Track Header Spacer */}
          <Box
            sx={{
              width: { xs: 80, sm: 130 },
              flexShrink: 0,
              px: 1.5,
              py: 0.6,
              borderRight: isDarkMode
                ? '1px solid rgba(255,255,255,0.08)'
                : '1px solid rgba(0,0,0,0.08)',
              display: 'flex',
              alignItems: 'center',
              gap: 0.8,
            }}
          >
            <LayersRoundedIcon sx={{ fontSize: 15, color: '#8b5cf6' }} />
            <Typography
              variant="caption"
              fontWeight={800}
              sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '0.68rem' }}
            >
              TRACKS
            </Typography>
          </Box>

          {/* Time Ruler Marks */}
          <Box
            sx={{
              flex: 1,
              position: 'relative',
              height: 26,
              borderBottom: isDarkMode
                ? '1px solid rgba(255,255,255,0.08)'
                : '1px solid rgba(0,0,0,0.08)',
              display: 'flex',
              alignItems: 'center',
              px: 1,
            }}
          >
            {[
              { pct: 0, label: '00:00:00' },
              { pct: 20, label: '00:00:36' },
              { pct: 40, label: '00:01:12' },
              { pct: 60, label: '00:01:48' },
              { pct: 80, label: '00:02:24' },
              { pct: 100, label: '00:03:00' },
            ].map((mark) => (
              <Box
                key={mark.pct}
                sx={{
                  position: 'absolute',
                  left: `${mark.pct}%`,
                  transform: mark.pct === 100 ? 'translateX(-100%)' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <Box
                  sx={{
                    width: 1,
                    height: 10,
                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.25)',
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.62rem',
                    fontWeight: 700,
                    color: isDarkMode ? '#64748b' : '#94a3b8',
                  }}
                >
                  {mark.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ══════════════════════════════════════════════════════════
            3. MULTI-TRACKS WORKSPACE & INTERACTIVE PLAYHEAD
            ══════════════════════════════════════════════════════════ */}
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            position: 'relative',
            cursor: 'crosshair',
          }}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setHoverPos(null)}
        >
          {/* TRACK HEADERS (Left Column) */}
          <Box
            sx={{
              width: { xs: 80, sm: 130 },
              flexShrink: 0,
              borderRight: isDarkMode
                ? '1px solid rgba(255,255,255,0.08)'
                : '1px solid rgba(0,0,0,0.08)',
              bgcolor: isDarkMode ? '#070c17' : '#f8fafc',
              zIndex: 2,
            }}
          >
            {/* Track V2 Header */}
            <Box
              sx={{
                height: 44,
                px: 1.2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: isDarkMode
                  ? '1px solid rgba(255,255,255,0.06)'
                  : '1px solid rgba(0,0,0,0.06)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                <AutoAwesomeRoundedIcon sx={{ fontSize: 13, color: '#ec4899' }} />
                <Typography variant="caption" fontWeight={900} sx={{ color: '#ec4899', fontSize: '0.72rem' }}>
                  V2 • VFX
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.2 }}>
                <IconButton
                  size="small"
                  onClick={() => setTracksMuted((prev) => ({ ...prev, v2: !prev.v2 }))}
                  sx={{ p: 0.3, color: tracksMuted.v2 ? '#ef4444' : '#64748b' }}
                >
                  {tracksMuted.v2 ? (
                    <VisibilityOffRoundedIcon sx={{ fontSize: 13 }} />
                  ) : (
                    <VisibilityRoundedIcon sx={{ fontSize: 13 }} />
                  )}
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setTracksLocked((prev) => ({ ...prev, v2: !prev.v2 }))}
                  sx={{ p: 0.3, color: tracksLocked.v2 ? '#eab308' : '#64748b' }}
                >
                  {tracksLocked.v2 ? (
                    <LockRoundedIcon sx={{ fontSize: 13 }} />
                  ) : (
                    <LockOpenRoundedIcon sx={{ fontSize: 13 }} />
                  )}
                </IconButton>
              </Box>
            </Box>

            {/* Track V1 Header */}
            <Box
              sx={{
                height: 52,
                px: 1.2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: isDarkMode
                  ? '1px solid rgba(255,255,255,0.06)'
                  : '1px solid rgba(0,0,0,0.06)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                <MovieFilterRoundedIcon sx={{ fontSize: 14, color: '#8b5cf6' }} />
                <Typography variant="caption" fontWeight={900} sx={{ color: '#8b5cf6', fontSize: '0.72rem' }}>
                  V1 • VIDEO
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.2 }}>
                <IconButton
                  size="small"
                  onClick={() => setTracksMuted((prev) => ({ ...prev, v1: !prev.v1 }))}
                  sx={{ p: 0.3, color: tracksMuted.v1 ? '#ef4444' : '#64748b' }}
                >
                  {tracksMuted.v1 ? (
                    <VisibilityOffRoundedIcon sx={{ fontSize: 13 }} />
                  ) : (
                    <VisibilityRoundedIcon sx={{ fontSize: 13 }} />
                  )}
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setTracksLocked((prev) => ({ ...prev, v1: !prev.v1 }))}
                  sx={{ p: 0.3, color: tracksLocked.v1 ? '#eab308' : '#64748b' }}
                >
                  {tracksLocked.v1 ? (
                    <LockRoundedIcon sx={{ fontSize: 13 }} />
                  ) : (
                    <LockOpenRoundedIcon sx={{ fontSize: 13 }} />
                  )}
                </IconButton>
              </Box>
            </Box>

            {/* Track A1 Header */}
            <Box
              sx={{
                height: 42,
                px: 1.2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: isDarkMode
                  ? '1px solid rgba(255,255,255,0.06)'
                  : '1px solid rgba(0,0,0,0.06)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                <AudiotrackRoundedIcon sx={{ fontSize: 13, color: '#06b6d4' }} />
                <Typography variant="caption" fontWeight={900} sx={{ color: '#06b6d4', fontSize: '0.72rem' }}>
                  A1 • DIALOG
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.2 }}>
                <IconButton
                  size="small"
                  onClick={() => setTracksMuted((prev) => ({ ...prev, a1: !prev.a1 }))}
                  sx={{ p: 0.3, color: tracksMuted.a1 ? '#ef4444' : '#64748b' }}
                >
                  {tracksMuted.a1 ? (
                    <VolumeOffRoundedIcon sx={{ fontSize: 13 }} />
                  ) : (
                    <VolumeUpRoundedIcon sx={{ fontSize: 13 }} />
                  )}
                </IconButton>
              </Box>
            </Box>

            {/* Track A2 Header */}
            <Box
              sx={{
                height: 40,
                px: 1.2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                <AudiotrackRoundedIcon sx={{ fontSize: 13, color: '#10b981' }} />
                <Typography variant="caption" fontWeight={900} sx={{ color: '#10b981', fontSize: '0.72rem' }}>
                  A2 • SCORE
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.2 }}>
                <IconButton
                  size="small"
                  onClick={() => setTracksMuted((prev) => ({ ...prev, a2: !prev.a2 }))}
                  sx={{ p: 0.3, color: tracksMuted.a2 ? '#ef4444' : '#64748b' }}
                >
                  {tracksMuted.a2 ? (
                    <VolumeOffRoundedIcon sx={{ fontSize: 13 }} />
                  ) : (
                    <VolumeUpRoundedIcon sx={{ fontSize: 13 }} />
                  )}
                </IconButton>
              </Box>
            </Box>
          </Box>

          {/* TIMELINE TRACKS CANVAS (Right Interactive Scrubbing Area) */}
          <Box
            ref={timelineRef}
            onMouseDown={handleMouseDown}
            onTouchMove={handleTouchMove}
            sx={{
              flex: 1,
              position: 'relative',
              overflow: 'hidden',
              bgcolor: isDarkMode ? '#060a14' : '#f1f5f9',
              userSelect: 'none',
            }}
          >
            {/* Cut Markers across tracks */}
            {cutPoints.map((cutPct) => (
              <Box
                key={cutPct}
                sx={{
                  position: 'absolute',
                  left: `${cutPct}%`,
                  top: 0,
                  bottom: 0,
                  width: 2,
                  bgcolor: '#ffffff',
                  boxShadow: '0 0 8px rgba(255,255,255,0.8)',
                  zIndex: 4,
                  pointerEvents: 'none',
                }}
              />
            ))}

            {/* ── TRACK V2: AI & VFX OVERLAY ── */}
            <Box
              sx={{
                height: 44,
                position: 'relative',
                borderBottom: isDarkMode
                  ? '1px solid rgba(255,255,255,0.06)'
                  : '1px solid rgba(0,0,0,0.06)',
                p: '4px 6px',
                opacity: tracksMuted.v2 ? 0.25 : 1,
                transition: 'opacity 0.2s ease',
              }}
            >
              {/* V2 Clip 1: AI Background Eraser */}
              <Box
                sx={{
                  position: 'absolute',
                  left: '6%',
                  width: '32%',
                  height: 'calc(100% - 8px)',
                  borderRadius: '6px',
                  background: 'linear-gradient(90deg, rgba(236, 72, 153, 0.85) 0%, rgba(219, 39, 119, 0.95) 100%)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  boxShadow: '0 2px 10px rgba(236, 72, 153, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  px: 1,
                  gap: 0.6,
                }}
              >
                <AutoAwesomeRoundedIcon sx={{ fontSize: 13, color: '#ffffff' }} />
                <Typography
                  sx={{
                    color: '#ffffff',
                    fontSize: '0.68rem',
                    fontWeight: 800,
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  }}
                >
                  AI Smart Cutout (99.8%)
                </Typography>
                {/* Keyframe diamonds */}
                <Box
                  sx={{
                    position: 'absolute',
                    right: 8,
                    width: 6,
                    height: 6,
                    bgcolor: '#ffffff',
                    transform: 'rotate(45deg)',
                    boxShadow: '0 0 6px #fff',
                  }}
                />
              </Box>

              {/* V2 Clip 2: 3D LUT Color Grade */}
              <Box
                sx={{
                  position: 'absolute',
                  left: '42%',
                  width: '36%',
                  height: 'calc(100% - 8px)',
                  borderRadius: '6px',
                  background: 'linear-gradient(90deg, rgba(245, 158, 11, 0.85) 0%, rgba(217, 119, 6, 0.95) 100%)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  boxShadow: '0 2px 10px rgba(245, 158, 11, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  px: 1,
                  gap: 0.6,
                }}
              >
                <Typography
                  sx={{
                    color: '#ffffff',
                    fontSize: '0.68rem',
                    fontWeight: 800,
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  }}
                >
                  🎞️ Kodak 500T 3D LUT
                </Typography>
              </Box>

              {/* V2 Clip 3: Title Motion Bloom */}
              <Box
                sx={{
                  position: 'absolute',
                  left: '81%',
                  width: '16%',
                  height: 'calc(100% - 8px)',
                  borderRadius: '6px',
                  background: 'linear-gradient(90deg, rgba(6, 182, 212, 0.85) 0%, rgba(14, 165, 233, 0.95) 100%)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  px: 0.8,
                }}
              >
                <Typography sx={{ color: '#ffffff', fontSize: '0.65rem', fontWeight: 800, whiteSpace: 'nowrap' }}>
                  Title Bloom
                </Typography>
              </Box>
            </Box>

            {/* ── TRACK V1: MAIN FOOTAGE FILMSTRIP ── */}
            <Box
              sx={{
                height: 52,
                position: 'relative',
                borderBottom: isDarkMode
                  ? '1px solid rgba(255,255,255,0.06)'
                  : '1px solid rgba(0,0,0,0.06)',
                p: '4px 6px',
                opacity: tracksMuted.v1 ? 0.25 : 1,
                transition: 'opacity 0.2s ease',
              }}
            >
              {/* V1 Clip 1: Scene 04 */}
              <Box
                sx={{
                  position: 'absolute',
                  left: '0%',
                  width: '28%',
                  height: 'calc(100% - 8px)',
                  borderRadius: '6px',
                  background: isDarkMode
                    ? 'linear-gradient(90deg, #4c1d95 0%, #6d28d9 100%)'
                    : 'linear-gradient(90deg, #c4b5fd 0%, #a78bfa 100%)',
                  border: '1.5px solid rgba(139, 92, 246, 0.8)',
                  display: 'flex',
                  alignItems: 'center',
                  px: 1,
                  justifyContent: 'space-between',
                  boxShadow: '0 2px 10px rgba(109, 40, 217, 0.35)',
                }}
              >
                <Typography
                  sx={{
                    color: '#ffffff',
                    fontSize: '0.72rem',
                    fontWeight: 900,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Clip_01_Night_Run.mov
                </Typography>
                <Chip
                  label="4K 60"
                  size="small"
                  sx={{ height: 16, fontSize: '0.58rem', fontWeight: 900, bgcolor: 'rgba(0,0,0,0.4)', color: '#fff' }}
                />
              </Box>

              {/* V1 Clip 2: Scene 08 with Speed Ramp */}
              <Box
                sx={{
                  position: 'absolute',
                  left: '30%',
                  width: '38%',
                  height: 'calc(100% - 8px)',
                  borderRadius: '6px',
                  background: isDarkMode
                    ? 'linear-gradient(90deg, #581c87 0%, #7e22ce 100%)'
                    : 'linear-gradient(90deg, #d8b4fe 0%, #c084fc 100%)',
                  border: '1.5px solid rgba(168, 85, 247, 0.8)',
                  display: 'flex',
                  alignItems: 'center',
                  px: 1,
                  justifyContent: 'space-between',
                  boxShadow: '0 2px 10px rgba(126, 34, 206, 0.35)',
                }}
              >
                <Typography
                  sx={{
                    color: '#ffffff',
                    fontSize: '0.72rem',
                    fontWeight: 900,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Clip_02_Cinematic_CloseUp.mov
                </Typography>
                <Chip
                  label="⚡ 2.5x Speed Ramp"
                  size="small"
                  sx={{ height: 16, fontSize: '0.58rem', fontWeight: 900, bgcolor: '#f59e0b', color: '#000' }}
                />
              </Box>

              {/* V1 Clip 3: Scene 12 Drone Aerial */}
              <Box
                sx={{
                  position: 'absolute',
                  left: '70%',
                  width: '29%',
                  height: 'calc(100% - 8px)',
                  borderRadius: '6px',
                  background: isDarkMode
                    ? 'linear-gradient(90deg, #431407 0%, #7c2d12 100%)'
                    : 'linear-gradient(90deg, #fed7aa 0%, #fdba74 100%)',
                  border: '1.5px solid rgba(249, 115, 22, 0.8)',
                  display: 'flex',
                  alignItems: 'center',
                  px: 1,
                  justifyContent: 'space-between',
                }}
              >
                <Typography
                  sx={{
                    color: '#ffffff',
                    fontSize: '0.72rem',
                    fontWeight: 900,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Clip_03_Aerial_Reveal.mov
                </Typography>
              </Box>
            </Box>

            {/* ── TRACK A1: DIALOG & FOLEY SOUNDWAVE ── */}
            <Box
              sx={{
                height: 42,
                position: 'relative',
                borderBottom: isDarkMode
                  ? '1px solid rgba(255,255,255,0.06)'
                  : '1px solid rgba(0,0,0,0.06)',
                p: '3px 6px',
                opacity: tracksMuted.a1 ? 0.25 : 1,
                transition: 'opacity 0.2s ease',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: '0%',
                  width: '98%',
                  height: 'calc(100% - 6px)',
                  borderRadius: '6px',
                  background: isDarkMode ? 'rgba(6, 182, 212, 0.18)' : 'rgba(6, 182, 212, 0.12)',
                  border: '1px solid rgba(6, 182, 212, 0.45)',
                  display: 'flex',
                  alignItems: 'center',
                  px: 1,
                  overflow: 'hidden',
                }}
              >
                {/* Simulated Audio Waveform Peaks */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', width: '100%', height: '100%' }}>
                  {Array.from({ length: 65 }).map((_, idx) => {
                    const waveHeight =
                      Math.abs(Math.sin(idx * 0.45) * 16 + Math.cos(idx * 0.8) * 8) + 4;
                    return (
                      <Box
                        key={idx}
                        sx={{
                          flex: 1,
                          height: `${waveHeight}px`,
                          bgcolor: '#06b6d4',
                          borderRadius: '1px',
                          opacity: isPlaying ? 0.9 : 0.6,
                        }}
                      />
                    );
                  })}
                </Box>
                <Typography
                  sx={{
                    position: 'absolute',
                    left: 10,
                    color: '#06b6d4',
                    fontSize: '0.66rem',
                    fontWeight: 800,
                    textShadow: '0 0 6px rgba(0,0,0,0.8)',
                  }}
                >
                  Actor_Dialog_Clean_Stereo.wav (AI De-Noise)
                </Typography>
              </Box>
            </Box>

            {/* ── TRACK A2: CINEMATIC SCORE ── */}
            <Box
              sx={{
                height: 40,
                position: 'relative',
                p: '3px 6px',
                opacity: tracksMuted.a2 ? 0.25 : 1,
                transition: 'opacity 0.2s ease',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: '0%',
                  width: '98%',
                  height: 'calc(100% - 6px)',
                  borderRadius: '6px',
                  background: isDarkMode ? 'rgba(16, 185, 129, 0.18)' : 'rgba(16, 185, 129, 0.12)',
                  border: '1px solid rgba(16, 185, 129, 0.45)',
                  display: 'flex',
                  alignItems: 'center',
                  px: 1,
                  overflow: 'hidden',
                }}
              >
                {/* Audio Waveform */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', width: '100%', height: '100%' }}>
                  {Array.from({ length: 70 }).map((_, idx) => {
                    const waveHeight =
                      Math.abs(Math.cos(idx * 0.3) * 14 + Math.sin(idx * 0.6) * 6) + 3;
                    return (
                      <Box
                        key={idx}
                        sx={{
                          flex: 1,
                          height: `${waveHeight}px`,
                          bgcolor: '#10b981',
                          borderRadius: '1px',
                          opacity: 0.65,
                        }}
                      />
                    );
                  })}
                </Box>
                <Typography
                  sx={{
                    position: 'absolute',
                    left: 10,
                    color: '#10b981',
                    fontSize: '0.66rem',
                    fontWeight: 800,
                    textShadow: '0 0 6px rgba(0,0,0,0.8)',
                  }}
                >
                  Soundtrack_Filmhook_Orchestral_Master.wav
                </Typography>
              </Box>
            </Box>

            {/* ── HOVER GHOST LINE (Shows timestamp on hover) ── */}
            {hoverPos !== null && (
              <Box
                sx={{
                  position: 'absolute',
                  left: `${hoverPos}%`,
                  top: 0,
                  bottom: 0,
                  width: '1px',
                  bgcolor: 'rgba(255, 255, 255, 0.4)',
                  pointerEvents: 'none',
                  zIndex: 8,
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: -22,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    bgcolor: 'rgba(15, 23, 42, 0.9)',
                    color: '#ffffff',
                    px: 0.8,
                    py: 0.2,
                    borderRadius: '4px',
                    fontSize: '0.6rem',
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    border: '1px solid rgba(255,255,255,0.2)',
                  }}
                >
                  {hoverTimecodeString}
                </Box>
              </Box>
            )}

            {/* ── 4. THE INTERACTIVE RED PLAYHEAD NEEDLE ── */}
            <Box
              sx={{
                position: 'absolute',
                left: `${progress}%`,
                top: 0,
                bottom: 0,
                width: '2px',
                bgcolor: '#ef4444',
                boxShadow: '0 0 12px rgba(239, 68, 68, 0.95), 0 0 24px rgba(239, 68, 68, 0.6)',
                zIndex: 10,
                pointerEvents: 'none',
                transform: 'translateX(-50%)',
                transition: isDragging ? 'none' : 'left 0.05s linear',
              }}
            >
              {/* Playhead Top Knob (Triangle needle) */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 0,
                  height: 0,
                  borderLeft: '7px solid transparent',
                  borderRight: '7px solid transparent',
                  borderTop: '11px solid #ef4444',
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.6))',
                }}
              />

              {/* Floating Current Timestamp Badge */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 4,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  bgcolor: '#ef4444',
                  color: '#ffffff',
                  fontSize: '0.58rem',
                  fontFamily: 'monospace',
                  fontWeight: 900,
                  px: 0.6,
                  py: 0.1,
                  borderRadius: '3px',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.5)',
                  whiteSpace: 'nowrap',
                }}
              >
                {timecodeString}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Helpful Hint bar below timeline */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mt: 1.5,
          px: 1,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: isDarkMode ? '#94a3b8' : '#64748b',
            fontSize: '0.74rem',
            display: 'flex',
            alignItems: 'center',
            gap: 0.6,
          }}
        >
          <span>💡</span>
          <strong>Pro-Tip:</strong> Click or drag anywhere along the timeline tracks to scrub footage. Hit the Play button to preview synchronized multi-track playback.
        </Typography>

        <Typography
          variant="caption"
          sx={{
            color: isDarkMode ? '#c084fc' : '#7c3aed',
            fontWeight: 800,
            fontSize: '0.72rem',
          }}
        >
          Filmhook Magnetic Engine • 60 FPS Real-Time Render
        </Typography>
      </Box>
    </Box>
  );
};

export default FilmhookNLETimelineScrubber;
